import axios from "axios";
import { getApp, getApps, initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import { toast } from "react-toastify";
import { handlePushNavigation } from "./pushNavigation";
import { API_BASE_URL } from "../../config";
import {
  FIREBASE_VAPID_KEY,
  FIREBASE_WEB_CONFIG,
  isWebPushConfigured,
} from "./firebaseWebConfig";

const SW_PATH = "/firebase-messaging-sw.js";
let cachedRegistration = null;
let foregroundUnsubscribe = null;

function getFirebaseApp() {
  if (getApps().length) return getApp();
  return initializeApp(FIREBASE_WEB_CONFIG);
}

async function getServiceWorkerRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  if (cachedRegistration) return cachedRegistration;
  cachedRegistration = await navigator.serviceWorker.register(SW_PATH);
  return cachedRegistration;
}

export async function getWebPushStatus() {
  if (!(await isSupported())) {
    return {
      status: "unavailable",
      label: "Unavailable",
      message: "This browser does not support web push notifications.",
    };
  }

  if (!isWebPushConfigured()) {
    return {
      status: "unavailable",
      label: "Not configured",
      message: "Web push is not configured for this build yet.",
    };
  }

  if (Notification.permission === "granted") {
    return {
      status: "enabled",
      label: "On",
      message: "You will receive alerts for bets, chat, and activity.",
    };
  }

  if (Notification.permission === "denied") {
    return {
      status: "blocked",
      label: "Blocked",
      message: "Notifications are blocked in your browser settings.",
    };
  }

  return {
    status: "disabled",
    label: "Off",
    message: "Turn on notifications to get bet and chat alerts.",
  };
}

function readAuthToken() {
  try {
    return (
      localStorage.getItem("betbond_app_token") ||
      localStorage.getItem("token") ||
      null
    );
  } catch {
    return null;
  }
}

function authHeaders(authToken) {
  return authToken ? { Authorization: `Bearer ${authToken}` } : {};
}

async function registerTokenWithBackend(token, authToken) {
  await axios.post(
    `${API_BASE_URL}/api/device-tokens`,
    {
      token,
      platform: "web",
    },
    { headers: authHeaders(authToken) }
  );
}

async function unregisterTokenFromBackend(token, authToken) {
  if (!token) return;
  try {
    // Prefer a snapshot auth token: logout clears storage before this cleanup runs.
    const bearer = authToken || readAuthToken();
    if (!bearer) return;
    await axios.delete(`${API_BASE_URL}/api/device-tokens`, {
      data: { token },
      headers: authHeaders(bearer),
    });
  } catch (error) {
    if (error?.response?.status === 401 || error?.response?.status === 404) return;
    console.warn("[WebPush] Failed to remove token:", error?.message);
  }
}

export async function enableWebPushNotifications() {
  if (!(await isSupported())) {
    return { ok: false, error: "Web push is not supported in this browser." };
  }

  if (!isWebPushConfigured()) {
    return {
      ok: false,
      error: "Web push is not configured. Add Firebase web env vars.",
    };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return { ok: false, error: "Notification permission was not granted." };
    }

    const registration = await getServiceWorkerRegistration();
    if (!registration) {
      return { ok: false, error: "Could not register the service worker." };
    }

    const messaging = getMessaging(getFirebaseApp());
    const token = await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!token) {
      return { ok: false, error: "Could not get a web push token." };
    }

    const authToken = readAuthToken();
    await registerTokenWithBackend(token, authToken);
    console.log("[WebPush] Device token registered");
    return { ok: true, token, authToken };
  } catch (error) {
    console.warn("[WebPush] Enable failed:", error?.message);
    return {
      ok: false,
      error:
        error?.response?.data?.error ||
        error?.message ||
        "Could not enable notifications.",
    };
  }
}

function bindForegroundMessages(messaging, navigate) {
  foregroundUnsubscribe?.();
  foregroundUnsubscribe = onMessage(messaging, (payload) => {
    const title =
      payload.notification?.title ||
      payload.data?.title ||
      payload.data?.notifTitle ||
      "BetBond";
    const body =
      payload.notification?.body ||
      payload.data?.body ||
      payload.data?.notifBody ||
      "";
    const message = body ? `${title}: ${body}` : title;

    toast.info(message, {
      onClick: () => {
        if (navigate) {
          handlePushNavigation(navigate, payload);
        } else if (payload?.data?.link) {
          window.location.assign(payload.data.link);
        }
      },
      style: { cursor: navigate || payload?.data?.link ? "pointer" : "default" },
    });
  });
}

function bindServiceWorkerNavigation(navigate) {
  if (!navigate || typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return () => {};
  }

  const onMessageFromSw = (event) => {
    const payload = event?.data;
    if (!payload || payload.type !== "PUSH_NAVIGATE") return;

    if (payload.data && handlePushNavigation(navigate, { data: payload.data })) {
      return;
    }

    if (payload.url) {
      navigate(payload.url);
      return;
    }

    if (payload.link) {
      try {
        const url = new URL(payload.link, window.location.origin);
        if (url.origin === window.location.origin) {
          navigate(`${url.pathname}${url.search}${url.hash}`);
        } else {
          window.location.assign(payload.link);
        }
      } catch {
        // ignore malformed links
      }
    }
  };

  navigator.serviceWorker.addEventListener("message", onMessageFromSw);
  return () => {
    navigator.serviceWorker.removeEventListener("message", onMessageFromSw);
  };
}

export function initWebPushNotifications({ navigate } = {}) {
  let cancelled = false;
  let activeToken = null;
  // Snapshot JWT while authenticated — logout clears storage before cleanup.
  let cachedAuthToken = null;
  const unbindSwNavigation = bindServiceWorkerNavigation(navigate);

  (async () => {
    if (!(await isSupported()) || !isWebPushConfigured()) return;

    const result = await enableWebPushNotifications();
    if (!result.ok || cancelled) return;

    activeToken = result.token;
    if (result.authToken) cachedAuthToken = result.authToken;
    bindForegroundMessages(getMessaging(getFirebaseApp()), navigate);
  })();

  const onVisible = () => {
    if (document.visibilityState !== "visible" || cancelled) return;
    enableWebPushNotifications().then((result) => {
      if (result.ok && result.token) activeToken = result.token;
      if (result.ok && result.authToken) cachedAuthToken = result.authToken;
    });
  };
  document.addEventListener("visibilitychange", onVisible);

  return async () => {
    cancelled = true;
    document.removeEventListener("visibilitychange", onVisible);
    unbindSwNavigation();
    foregroundUnsubscribe?.();
    foregroundUnsubscribe = null;
    await unregisterTokenFromBackend(activeToken, cachedAuthToken);
    activeToken = null;
    cachedAuthToken = null;
  };
}

export function watchWebPushStatus(onChange) {
  let active = true;

  const refresh = async () => {
    const next = await getWebPushStatus();
    if (active) onChange(next);
  };

  refresh();
  document.addEventListener("visibilitychange", refresh);

  return () => {
    active = false;
    document.removeEventListener("visibilitychange", refresh);
  };
}

/**
 * Ask the backend to send a test push to this user's registered devices.
 * Keep the tab focused to verify the foreground toast path.
 */
export async function sendTestWebPush({ platforms = ["web"] } = {}) {
  try {
    const authToken = readAuthToken();
    if (!authToken) {
      return { ok: false, error: "You must be logged in to send a test push." };
    }

    const { data } = await axios.post(
      `${API_BASE_URL}/api/device-tokens/test`,
      { platforms },
      { headers: authHeaders(authToken) }
    );

    return { ok: true, result: data?.result, message: data?.message };
  } catch (error) {
    return {
      ok: false,
      error:
        error?.response?.data?.error ||
        error?.message ||
        "Could not send test push.",
      result: error?.response?.data?.result,
    };
  }
}
